/**
 * @fileoverview Product statistics detail page with bento-grid layout.
 * @module features/home/pages/ProductStatisticsPage
 */

import { type ReactElement, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { ScrollReveal } from '@/shared/components/ui'
import { DashboardDataProvider } from '@/shared/providers'
import { useProductGroupsMetadata } from '@/shared/hooks/useProductGroupsMetadata'
import { useFilter } from '@/shared/hooks/useFilter'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { prefixWithOrg, queryKeys } from '@/shared/utils/queryKeys'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import {
  statisticsService,
  type UnifiedStatisticsFilterDTO,
} from '@/lib/statisticsService'
import {
  useProductAggregate,
  useProductTimeSeries,
  useProductByCountry,
  useProductPreviousTotals,
} from '@/features/home/hooks/useProductStatistics'
import { ProductHero } from '@/features/home/components/product-statistics/ProductHero'
import { ProductBreakdowns } from '@/features/home/components/product-statistics/ProductBreakdowns'
import { RevenueOverTimeChart } from '@/features/home/components/product-statistics/RevenueOverTimeChart'
import { VariantBreakdownTable } from '@/features/home/components/product-statistics/VariantBreakdownTable'
import { ProductAllocationBreakdown } from '@/features/home/components/product-statistics/ProductAllocationBreakdown'

interface ProductRouteState {
  memberSkus?: string[]
  productName?: string
  imageUrl?: string | null
}

/**
 * Product statistics detail page showing KPIs, cost breakdown, time series,
 * and variant-level performance. Accessible from the performance table.
 */
export function ProductStatisticsPage(): ReactElement {
  return (
    <DashboardDataProvider>
      <ProductStatisticsContent />
    </DashboardDataProvider>
  )
}

function ProductStatisticsContent(): ReactElement {
  const { t } = useTranslation('features-home')
  const navigate = useNavigate()
  const { productId } = useParams<{ productId: string }>()
  const location = useLocation()
  const state = (location.state ?? {}) as ProductRouteState
  const { getGroup } = useProductGroupsMetadata()

  // Resolve product metadata: prefer route state, fall back to dashboard context
  const decodedProductId = productId ? decodeURIComponent(productId) : ''
  const group = useMemo(() => getGroup(decodedProductId), [getGroup, decodedProductId])

  const memberSkus = state.memberSkus?.length ? state.memberSkus : (group?.members ?? [])
  const productName = state.productName ?? group?.name ?? decodedProductId
  const imageUrl = state.imageUrl !== undefined ? state.imageUrl : (group?.imageUrl ?? null)

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0) }, [])

  // ESC key → navigate back (preserves scroll position)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        navigate(-1)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  // Data queries
  const aggregate = useProductAggregate(memberSkus)
  const timeSeries = useProductTimeSeries(memberSkus)
  const byCountry = useProductByCountry(memberSkus)
  const previousTotals = useProductPreviousTotals(memberSkus)

  // ORG-WIDE allocation source.
  // The collection allocator derives each product's share from the sales it is
  // given, and the statistics service filters sales by the query's `skus`/`tags`
  // BEFORE allocation. A product-scoped query therefore mis-computes shares, so
  // the breakdown MUST read `collectionAllocation` from an unfiltered (no skus,
  // no tags, no countries) statistics response and filter to this product on FE.
  const { activeOrgId } = useActiveOrg()
  const { startDate, endDate } = useFilter()

  const orgWideFilter = useMemo<UnifiedStatisticsFilterDTO>(() => {
    const f: UnifiedStatisticsFilterDTO = { includeAllocated: true }
    if (startDate) f.startDate = toDateOnlyStringLocal(startDate)
    if (endDate) f.endDate = toDateOnlyStringLocal(endDate)
    return f
  }, [startDate, endDate])

  const orgWideAllocationQuery = useQuery({
    queryKey: activeOrgId
      ? prefixWithOrg(
          activeOrgId as unknown as string,
          queryKeys.salesStatistics(orgWideFilter, ['PRODUCT'])
        )
      : queryKeys.salesStatistics(orgWideFilter, ['PRODUCT']),
    queryFn: () => statisticsService.getSalesStatistics(orgWideFilter, ['PRODUCT']),
    enabled: Boolean(activeOrgId && orgWideFilter.startDate && orgWideFilter.endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const orgWideAllocation = orgWideAllocationQuery.data?.collectionAllocation

  // Allocated ad spend attributable to THIS product, summed from the org-wide
  // (correctly computed) allocation members.
  const allocatedForProduct = useMemo(() => {
    if (!orgWideAllocation) return 0
    return orgWideAllocation.groups.reduce((sum, group) => {
      const member = group.members.find((m) => m.productKey === decodedProductId)
      return sum + (member?.allocated ?? 0)
    }, 0)
  }, [orgWideAllocation, decodedProductId])

  const totals = aggregate.totals

  // Keep the breakdown internally consistent with the page's displayed ad spend:
  // the page's own (product-filtered) ad-spend total may itself fold in a
  // mis-computed collection portion, so subtract the org-wide allocated amount to
  // recover the direct figure. Direct + Allocated == the page's ad-spend total only
  // when advertising >= allocatedForProduct; otherwise direct is clamped to 0 (so the
  // displayed total may exceed the filtered ad-spend figure).
  const directAdSpend = Math.max(0, (totals.advertising ?? 0) - allocatedForProduct)

  const hasBreakdowns =
    byCountry.countries.length > 0 ||
    Object.values(totals.platformBreakdown ?? {}).some((v) => v > 0)

  const isLoading = aggregate.isLoading || timeSeries.isLoading

  if (memberSkus.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">{t('productStatistics.noData')}</p>
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('productStatistics.backToPerformance')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
        {/* Back navigation */}
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
          </div>
        </ScrollReveal>

        {/* Loading overlay */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Content */}
        {!isLoading && (
          <div className="space-y-8">
            {/* Hero: image, name, unified metrics panel with period deltas */}
            <ScrollReveal delay={0.1} blur>
              <ProductHero
                productId={decodedProductId}
                productName={productName}
                imageUrl={imageUrl}
                metrics={totals}
                previous={previousTotals.totals}
              />
            </ScrollReveal>

            {/* Country / platform breakdowns. Gated here so the animated wrapper
                isn't rendered (no empty gap) when both splits are empty. */}
            {hasBreakdowns && (
              <ScrollReveal delay={0.1} blur>
                <ProductBreakdowns
                  countries={byCountry.countries}
                  platformBreakdown={totals.platformBreakdown ?? {}}
                />
              </ScrollReveal>
            )}

            {/* Revenue & Profit Over Time — hidden for single-day view */}
            {timeSeries.data.length > 1 && (
              <ScrollReveal delay={0.1} blur>
                <RevenueOverTimeChart data={timeSeries.data} />
              </ScrollReveal>
            )}

            {/* Variant Breakdown */}
            {aggregate.variants.length > 1 && (
              <ScrollReveal delay={0.1} blur>
                <VariantBreakdownTable
                  variants={aggregate.variants}
                  dimensionGroup={aggregate.dimensionGroup}
                />
              </ScrollReveal>
            )}

            {/* Ad-spend transparency: direct vs collection-allocated. Pinned to the
                bottom — it's strictly informational, not an analysis surface.
                Gated on allocatedForProduct so the animated wrapper isn't rendered
                (and no empty gap appears) when there's no allocation. */}
            {allocatedForProduct > 0 && (
              <ScrollReveal delay={0.1} blur>
                <ProductAllocationBreakdown
                  productId={decodedProductId}
                  directAdSpend={directAdSpend}
                  allocation={orgWideAllocation}
                />
              </ScrollReveal>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
